import { useState, useEffect, useMemo, type FC, type MouseEvent } from "react";
import { calculateTimeDifferenceIsSeconds } from "../../../utils/timeUtil";
import { Message, type CalendarData } from "../../../core/types";
import { useCore } from "../../../contexts/useCore";
import ChatMessageListUserItem from "./ChatMessageListUserItem";
import ChatMessageListAssistantItem from "./ChatMessageListAssistantItem";
import ChatMessageListFeedbackItem from "./ChatMessageListFeedbackItem";
import ChatMessageListJoinedEventItem from "./ChatMessageListJoinedEventItem";
import ChatMessageListToolCallItem from "./ChatMessageListToolCallItem";
import ChatMessageListToolAttribution from "./ChatMessageListToolAttribution";
import CalendarMessage from "./CalendarMessage";
import ChatMessageListLoader from "./ChatMessageListLoader";
import ChatMessageListOptions from "./ChatMessageListOptions";
import ChatMessageListRatingDisplayItem from "./ChatMessageListRatingDisplayItem";
import { Slot, Templated } from "../../../templating";

/** A message that survived the null-contents filter, so it renders as text. */
type RenderableMessage = Omit<Message, "contents"> & { contents: string };

interface ChatMessageListProps {
  messageThreadId: string;
  messages: Message[];
  threadClosedAt: string | null;
  rating: number | null;
}

const ChatMessageList: FC<ChatMessageListProps> = ({ messageThreadId, messages, threadClosedAt, rating }) => {
  const core = useCore();
  const threadId = parseInt(messageThreadId, 10);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<Message | null>(null);

  const messagesWithContent = useMemo(
    () => messages
      .filter((message): message is RenderableMessage => message.contents !== null)
      .map((message) => {
        const dataAsOptions = message.metadata?.type === "options"
          && Array.isArray(message.metadata.data)
          ? (message.metadata.data as string[])
          : undefined;
        const options = dataAsOptions ?? message.metadata?.options;

        const contents = message.contents;

        return {
          ...message,
          metadata: {
            ...message.metadata,
            options: options && options.length > 0 ? options : [],
          },
          contents,
        } as RenderableMessage;
      }),
    [messages]
  );

  const messagesUpdatedTimestamps = useMemo(
    () => messagesWithContent.map((message) => message.updated_at),
    [messagesWithContent]
  );

  const lastMessageOptions = useMemo(() => {
    return messagesWithContent[messagesWithContent.length - 1]?.metadata?.options ?? [];
  }, [messagesWithContent]);

  const lastCalendarId = useMemo(() => {
    const calendars = messagesWithContent.filter(
      (m) => m.role === "assistant" && m.metadata?.type === "calendar" && m.metadata?.data != null
    );
    return calendars[calendars.length - 1]?.id;
  }, [messagesWithContent]);

  const handleLinkClick = (event: MouseEvent<HTMLDivElement>): void => {
    // `closest` rather than `matches` so clicks on nested elements inside the
    // anchor (e.g. <a data-url="..."><strong>Open</strong></a>) still resolve
    // to the link. `matches` would only match when the anchor itself is the
    // direct event target.
    const target = event.target as Element | null;
    const link = target?.closest("a[data-url]") as HTMLAnchorElement | null;

    if (link) {
      event.preventDefault();
      const url = link.getAttribute("data-url")!;
      const linkTrackingUrl = core.getLinkTrackingUrl(parseInt(messageThreadId, 10), url);
      // Never add "noreferrer" here: the /click request must carry a Referer, which the
      // backend external-link guard (LAS-33) uses to trust a genuine in-widget click and
      // skip the interstitial. "noopener" alone keeps the tab isolated (LAS-2819).
      const newWindow = window.open(linkTrackingUrl, "_blank", "noopener");
      // For older browsers that don't support the "noopener" window feature
      if (newWindow) newWindow.opener = null;
    }
  };

  const handleOptionClick = (option: string): void => {
    void core.sendMessage(option);
  };

  const handleCalendarSlotClick = (message: string): void => {
    void core.sendMessage(message);
  };

  const handleFinishedTyping = (): void => {
    if (pendingMessage?.is_finished) {
      setPendingMessage(null);
    }
  };

  const findLastAssistantOrEventMessage = (ignoredMessage: Message): RenderableMessage | null => {
    for (let i = messagesWithContent.length - 1; i >= 0; i--) {
      const message = messagesWithContent[i];
      if ((message.role === "event" || message.role === "assistant") && message.id !== ignoredMessage.id) {
        return message;
      }
    }
    return null;
  };

  const isWaitingArtificialAgentReply = (lastMessage: Message): boolean => {
    const lastAssistantOrEventMessage = findLastAssistantOrEventMessage(lastMessage);
    const lastAssistantMessageFromArtificialAgent = lastAssistantOrEventMessage !== null && lastAssistantOrEventMessage.role !== "event" && !lastAssistantOrEventMessage.sender;
    const lastMessageRecentlyUpdated = calculateTimeDifferenceIsSeconds(lastMessage.updated_at) <= 10;
    return lastMessageRecentlyUpdated && lastMessage.role === "user" && lastAssistantMessageFromArtificialAgent;
  };

  const isWaitingArtificialAgentTyping = (lastMessage: Message): boolean => {
    const lastMessageRecentlyUpdated = calculateTimeDifferenceIsSeconds(lastMessage.updated_at) <= 5;
    return lastMessageRecentlyUpdated && lastMessage.role === "assistant" && !lastMessage.sender;
  };

  useEffect(() => {
    if (messagesWithContent.length === 0) {
      return;
    }

    const lastMessage = messagesWithContent[messagesWithContent.length - 1];
    const waitingArtificialAgentReply = messagesWithContent.length === 1 && lastMessage.role === "user" || isWaitingArtificialAgentReply(lastMessage);
    const waitingArtificialAgentTyping = isWaitingArtificialAgentTyping(lastMessage);
    setIsLoading(waitingArtificialAgentReply);
    setPendingMessage(waitingArtificialAgentTyping ? lastMessage : null);
  }, [messagesUpdatedTimestamps]);

  return (
    <Templated id="lastbot-message-list">
      <>
      <div
        onClick={handleLinkClick}
        className="flex flex-col gap-y-6 md:max-w-[var(--lastbot-content-max-width)] md:w-full md:self-center"
      >
        <Slot name="message-list-items">
        {messagesWithContent.map(({ id, role, contents, sender, attachment, metadata }, index) => {
          if (role === "user" && contents.match(/\{\{★+\}\}/)) {
            return <ChatMessageListRatingDisplayItem key={id} message={contents} />;
          }
          if (role === "user") {
            return <ChatMessageListUserItem key={id} messageId={id} message={contents} attachment={attachment} />;
          }
          if (role === "event" && metadata?.event === "agent_joined") {
            return <ChatMessageListJoinedEventItem key={id} message={contents} />;
          }
          if (role === "assistant" && metadata?.field_name === "tool_call") {
            return (
              <ChatMessageListToolCallItem
                key={id}
                toolName={contents}
                isTyping={pendingMessage?.id === id}
                isExiting={messagesWithContent.slice(index + 1).some(
                  (m) => m.role === "assistant" && m.metadata?.field_name !== "tool_call"
                )}
                onFinished={handleFinishedTyping}
              />
            );
          }
          if (role === "assistant" && metadata?.field_name === "submit_rating") {
            return (
              <ChatMessageListFeedbackItem
                key={id}
                messageId={id}
                threadId={threadId}
                message={contents}
                options={metadata.options}
                rating={rating}
                isTyping={pendingMessage?.id === id}
                onFinished={handleFinishedTyping}
                onSubmitRating={handleOptionClick}
              />
            );
          }
          if (role === "assistant" || (role === "event" && metadata?.event === "agent_invited")) {
            const isTyping = pendingMessage?.id === id;
            // Render the interactive CalendarMessage whenever the message carries
            // calendar metadata — including during the brief window where the
            // metadata update (which runs after streaming) refreshes updated_at
            // and causes isTyping to flip back to true. Calendar metadata is only
            // set server-side after streaming completes, so this never shows
            // prematurely. The disabled check ignores the message's own pending
            // state (a metadata-only refresh) and only blocks on a *different*
            // message being actively typed.
            const isCalendar = role === "assistant" && metadata?.type === "calendar" && metadata?.data != null;
            if (isCalendar) {
              return (
                <CalendarMessage
                  key={id}
                  messageId={id}
                  threadId={threadId}
                  isTyping={isTyping}
                  data={metadata.data as CalendarData}
                  message={contents}
                  onSlotSelect={handleCalendarSlotClick}
                  disabled={!!threadClosedAt || id !== lastCalendarId || (pendingMessage !== null && pendingMessage.id !== id)}
                />
              );
            }
            return (
              <ChatMessageListAssistantItem
                key={id}
                messageId={id}
                threadId={threadId}
                message={contents}
                sender={sender ?? null}
                attachment={attachment}
                isTyping={isTyping}
                onFinished={handleFinishedTyping}
              >
                {role === "assistant" && !isTyping && metadata?.tools_used && metadata.tools_used.length > 0 && (
                  <ChatMessageListToolAttribution tools={metadata.tools_used} />
                )}
              </ChatMessageListAssistantItem>
            );
          }
          return null;
        })}
        </Slot>
        {isLoading && <ChatMessageListLoader />}
      </div>
      {!threadClosedAt &&
      pendingMessage === null &&
      (messagesWithContent.length > 0 && messagesWithContent[messagesWithContent.length - 1].metadata?.field_name !== "submit_rating") &&
      (
        <div className="md:max-w-[var(--lastbot-content-max-width)] md:w-full md:self-center">
          <ChatMessageListOptions
            options={lastMessageOptions}
            onClick={handleOptionClick}
          />
        </div>
      )}
      </>
    </Templated>
  );
};

export default ChatMessageList;
